import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider, SignInButton, UserButton, useUser, useAuth } from '@clerk/clerk-react';
import App from './App';
import PreviewLanding from './PreviewLanding';
import { canViewToolDirectory } from '../core/access';
import './styles.css';

export const PUBLIC_CLERK_PUBLISHABLE_KEY = 'pk_live_Y2xlcmsuMjQwLm9yZyQ=';

const publishableKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ||
  (import.meta.env.PROD ? PUBLIC_CLERK_PUBLISHABLE_KEY : undefined);

const path = window.location.pathname;
const legacyLanding = path === '/tools' || path === '/notify';
/**
 * The query string rides along. A teacher the study app sent here arrives with
 * `from=study-guide`, and dropping it across the sign-in modal would take their way
 * back with it — at the exact moment signing in made the page useful.
 */
const accountReturnPath =
  (legacyLanding || path === '/preview' ? path : '/') + window.location.search;

function ClerkLanding() {
  const { isSignedIn, isLoaded, user } = useUser();
  const { getToken } = useAuth();
  const email = user?.primaryEmailAddress?.emailAddress;

  /**
   * Handed to PostHog so a flag targeting a person property can evaluate at all —
   * without it every visitor here is anonymous and such a flag matches nobody.
   *
   * `undefined` until Clerk has loaded, which is not the same as "signed out":
   * treating the loading state as a sign-out would reset the identity on every load.
   */
  const identity = !isLoaded ? undefined : isSignedIn && user ? { id: user.id, email } : null;

  const renderAccountAction = (onLogin: () => void) => isSignedIn ? (
    <UserButton afterSignOutUrl="/" />
  ) : (
    <SignInButton mode="modal" forceRedirectUrl={accountReturnPath} signUpForceRedirectUrl={accountReturnPath}>
      <button className={legacyLanding ? 'login-button' : 'preview-login'} type="button" onClick={onLogin}>
        Log in
      </button>
    </SignInButton>
  );

  return legacyLanding ? (
    <App
      showTools={isSignedIn === true && canViewToolDirectory(email)}
      renderAccountAction={renderAccountAction}
    />
  ) : (
    <PreviewLanding renderAccountAction={renderAccountAction} identity={identity} getToken={getToken}
      renderNotifySignIn={(onClick, label) => (
        <SignInButton mode="modal" forceRedirectUrl={accountReturnPath} signUpForceRedirectUrl={accountReturnPath}>
          <button className="preview-notify" type="button" disabled={!isLoaded} onClick={onClick}>{label}</button>
        </SignInButton>
      )} />
  );
}

/**
 * Browser tests only: the e2e build compiles in a signed-in teacher, and `?e2e=signed-out`
 * renders the same page signed out. Undefined in every other build, so this is dead code
 * there and Vite drops it.
 */
const e2eIdentity = import.meta.env.VITE_E2E_IDENTITY;
function E2ELanding({ token }: { token: string }) {
  const [id, email] = token.split(':');
  const signedOut = new URLSearchParams(window.location.search).get('e2e') === 'signed-out';
  return <PreviewLanding identity={signedOut ? null : { id: id!, email }} getToken={async () => (signedOut ? null : 'e2e-token')} />;
}

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');
const reactRoot = createRoot(root);

reactRoot.render(
  <StrictMode>
    {e2eIdentity ? (
      <E2ELanding token={e2eIdentity} />
    ) : publishableKey ? (
      <ClerkProvider
        publishableKey={publishableKey}
        signInForceRedirectUrl={accountReturnPath}
        signUpForceRedirectUrl={accountReturnPath}
        signInFallbackRedirectUrl={accountReturnPath}
        signUpFallbackRedirectUrl={accountReturnPath}
        afterSignOutUrl="/"
      >
        <ClerkLanding />
      </ClerkProvider>
    ) : legacyLanding ? (
      <App />
    ) : (
      <PreviewLanding />
    )}
  </StrictMode>,
);
